import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { ChevronLeft, Smartphone, Search, Calendar as CalendarIcon, CheckCircle2, Check } from "lucide-react";
import { format, addDays } from "date-fns";
import { api, formatINR, ISSUE_OPTIONS, issueLabel } from "@/lib/api";

const CONDITION_QUESTIONS = [
  {
    key: "screen",
    label: "Screen condition",
    options: [
      { value: "perfect", label: "Perfect", desc: "No scratches at all" },
      { value: "minor_scratches", label: "Minor scratches", desc: "Visible under light" },
      { value: "cracked", label: "Cracked or chipped", desc: "Clear damage" },
    ],
  },
  {
    key: "body",
    label: "Body condition",
    options: [
      { value: "perfect", label: "Perfect", desc: "Looks like new" },
      { value: "minor_scratches", label: "Minor scratches", desc: "Small wear marks" },
      { value: "dents", label: "Dents or heavy marks", desc: "Visible damage" },
    ],
  },
  {
    key: "battery",
    label: "Battery health",
    options: [
      { value: "good", label: "Good", desc: "Lasts full day" },
      { value: "average", label: "Average", desc: "Lasts half a day" },
      { value: "poor", label: "Poor", desc: "Drains quickly" },
    ],
  },
  {
    key: "working",
    label: "Is the phone fully working?",
    options: [
      { value: "yes", label: "Fully working", desc: "Calls, cameras, buttons all work" },
      { value: "no", label: "Not fully working", desc: "One or more things don't work" },
    ],
  },
];

function IssuesPicker({ issues, onToggle, pct }) {
  const tile = (active) =>
    `cursor-pointer flex items-center gap-2.5 p-3 rounded-lg border-2 text-left transition-colors ${
      active ? "border-[#1a56db] bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
    }`;
  const box = (active) =>
    `w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center ${
      active ? "bg-[#1a56db] border-[#1a56db] text-white" : "border-slate-300"
    }`;
  return (
    <div data-testid="lcost-issues-section" className="p-5 rounded-2xl bg-white border border-slate-200">
      <Label className="text-sm font-semibold text-slate-900">Functional or physical problems</Label>
      <p className="text-xs text-slate-500 mt-1">Select all that apply. Each problem reduces the price by {pct}%.</p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {ISSUE_OPTIONS.map((o) => {
          const active = issues.includes(o.value);
          return (
            <button type="button" key={o.value} data-testid={`lcost-issue-${o.value}`} onClick={() => onToggle(o.value)} className={tile(active)}>
              <span className={box(active)}>{active && <Check className="w-3.5 h-3.5" />}</span>
              <span className="text-sm font-medium text-slate-900">{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Sell() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState([]);
  const [meta, setMeta] = useState({ cities: [], time_slots: [] });
  const [brand, setBrand] = useState(null);
  const [model, setModel] = useState(null);
  const [condition, setCondition] = useState({ issues: [] });
  const toggleIssue = (v) => {
    setCondition((c) => ({
      ...c,
      issues: c.issues.includes(v) ? c.issues.filter((i) => i !== v) : [...c.issues, v],
    }));
  };
  const goBack = () => {
    if (step === 1) return navigate("/");
    if (step === 3.5) return setStep(3);
    if (step === 4) return setStep(condition.working === "no" ? 3.5 : 3);
    setStep(step - 1);
  };
  const [quote, setQuote] = useState(null);
  const [modelSearch, setModelSearch] = useState("");
  const [customer, setCustomer] = useState({
    name: "", phone: "", email: "", address: "", city: "",
  });
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupSlot, setPickupSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [brandsRes, metaRes] = await Promise.all([
          api.get("/catalog/brands"),
          api.get("/catalog/meta"),
        ]);
        setBrands(brandsRes.data.brands);
        setMeta(metaRes.data);
      } catch (e) {
        toast.error("Could not load phone catalog");
      }
    })();
  }, []);

  const filteredModels = useMemo(() => {
    if (!brand) return [];
    const q = modelSearch.trim().toLowerCase();
    return brand.models.filter((m) => !q || m.name.toLowerCase().includes(q));
  }, [brand, modelSearch]);

  const allConditionAnswered =
    CONDITION_QUESTIONS.every((c) => condition[c.key]);

  const getQuote = async () => {
    try {
      const res = await api.post("/quote", {
        phone_id: model.id,
        condition: condition.working === "yes" ? { ...condition, issues: [] } : condition,
      });
      setQuote(res.data);
      setStep(4);
    } catch (e) {
      toast.error("Could not calculate quote");
    }
  };

  const submitOrder = async () => {
    if (!customer.name || !customer.phone || !customer.email ||
        !customer.address || !customer.city || !pickupDate || !pickupSlot) {
      toast.error("Please fill all pickup details");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/orders", {
        phone_id: model.id,
        condition,
        customer,
        pickup_date: format(pickupDate, "yyyy-MM-dd"),
        pickup_slot: pickupSlot,
      });
      navigate(`/confirmation/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not book pickup");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Progress bar */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={goBack}
              className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900"
              data-testid="lcost-sell-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </button>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Step {Math.min(Math.floor(step), 5)} of 5
            </div>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#1a56db] to-[#0e9f6e] transition-all duration-500"
              style={{ width: `${(Math.min(step, 5) / 5) * 100}%` }}
            />
          </div>
        </div>

        {/* STEP 1 - BRAND */}
        {step === 1 && (
          <div className="step-slide">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Which brand is your phone?
            </h1>
            <p className="mt-2 text-slate-600">Pick a brand to continue.</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
              {brands.map((b) => (
                <button
                  key={b.brand_id}
                  data-testid={`lcost-brand-select-${b.brand_id}`}
                  onClick={() => {
                    setBrand(b);
                    setModel(null);
                    setStep(2);
                  }}
                  className="brand-pill card-hover p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white text-left"
                >
                  <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-3">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div className="font-display font-bold text-slate-900 text-sm sm:text-base">
                    {b.brand_name}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {b.models.length} models
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 - MODEL */}
        {step === 2 && brand && (
          <div className="step-slide">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#1e429f] uppercase tracking-wider">
              <span>{brand.brand_name}</span>
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1">
              Pick your exact model
            </h1>
            <div className="relative mt-5">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="lcost-model-search"
                placeholder="Search model..."
                value={modelSearch}
                onChange={(e) => setModelSearch(e.target.value)}
                className="pl-10 h-11 bg-white"
              />
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {filteredModels.map((m) => (
                <button
                  key={m.id}
                  data-testid={`lcost-model-select-${m.id}`}
                  onClick={() => {
                    setModel(m);
                    setStep(3);
                  }}
                  className="card-hover p-4 rounded-xl border border-slate-200 bg-white text-left"
                >
                  <div className="font-semibold text-slate-900 text-sm">{m.name}</div>
                  <div className="mt-1.5 flex items-baseline gap-1">
                    <span className="text-xs text-slate-500">Up to</span>
                    <span className="font-display font-bold text-[#057a55] bg-[#def7ec] px-1.5 rounded-md">
                      {formatINR(m.base_price)}
                    </span>
                  </div>
                </button>
              ))}
              {filteredModels.length === 0 && (
                <div className="col-span-full text-center py-10 text-slate-500 text-sm">
                  No models match “{modelSearch}”.
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 - CONDITION */}
        {step === 3 && model && (
          <div className="step-slide">
            <div className="text-xs font-semibold text-[#1e429f] uppercase tracking-wider">
              {brand.brand_name} · {model.name}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1">
              Tell us the condition
            </h1>
            <p className="mt-2 text-slate-600 text-sm">
              Be honest — your quote is final if your answers match at pickup.
            </p>
            <div className="mt-6 space-y-5">
              {CONDITION_QUESTIONS.map((q) => (
                <div key={q.key} className="p-5 rounded-2xl bg-white border border-slate-200">
                  <Label className="text-sm font-semibold text-slate-900">{q.label}</Label>
                  <RadioGroup
                    value={condition[q.key] || ""}
                    onValueChange={(v) => setCondition((c) => ({ ...c, [q.key]: v }))}
                    className={`mt-3 grid grid-cols-1 ${q.options.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-2.5`}
                  >
                    {q.options.map((o) => (
                      <label
                        key={o.value}
                        data-testid={`lcost-condition-opt-${q.key}-${o.value}`}
                        className={`cursor-pointer flex items-start gap-2.5 p-3 rounded-lg border-2 transition ${
                          condition[q.key] === o.value
                            ? "border-[#1a56db] bg-blue-50"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <RadioGroupItem value={o.value} className="mt-0.5" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-900">{o.label}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{o.desc}</div>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              ))}
            </div>
            <Button
              data-testid="lcost-calculate-quote-btn"
              onClick={condition.working === "no" ? () => setStep(3.5) : getQuote}
              disabled={!allConditionAnswered}
              className="mt-6 w-full h-12 btn-brand text-white font-semibold text-base rounded-xl"
            >
              {condition.working === "no" ? "Continue" : "Get my instant quote"}
            </Button>
          </div>
        )}

        {/* STEP 3.5 - ISSUES (only when not fully working) */}
        {step === 3.5 && model && (
          <div className="step-slide">
            <div className="text-xs font-semibold text-[#1e429f] uppercase tracking-wider">
              {brand.brand_name} · {model.name}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mt-1">
              What's not working?
            </h1>
            <p className="mt-2 text-slate-600 text-sm">
              Tap every problem your phone has.
            </p>
            <div className="mt-6">
              <IssuesPicker issues={condition.issues} onToggle={toggleIssue} pct={meta.issue_deduction_pct ?? 10} />
            </div>
            <Button
              data-testid="lcost-issues-quote-btn"
              onClick={getQuote}
              disabled={condition.issues.length === 0}
              className="mt-6 w-full h-12 btn-brand text-white font-semibold text-base rounded-xl"
            >
              Get my instant quote
            </Button>
          </div>
        )}

        {/* STEP 4 - QUOTE */}
        {step === 4 && quote && (
          <div className="step-slide">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-[#1e429f] text-xs font-semibold uppercase tracking-wider">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Guaranteed quote
              </div>
              <div className="mt-4 text-sm font-medium text-slate-500">
                {quote.brand_name} · {quote.model_name}
              </div>
              <div
                data-testid="lcost-quote-price-value"
                className="quote-reveal font-display text-5xl sm:text-6xl font-black text-white bg-gradient-to-r from-[#1a56db] to-[#0e9f6e] inline-block px-5 py-1 rounded-2xl tracking-tight mt-3"
              >
                {formatINR(quote.amount)}
              </div>
              <div className="mt-2 text-sm text-slate-500">
                Base price {formatINR(quote.base_price)}
              </div>
            </div>

            <div className="mt-8 p-5 rounded-2xl bg-white border border-slate-200">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Your answers
              </div>
              <div className="space-y-2.5">
                {quote.breakdown.map((b) => {
                  const isIssue = b.category === "issue";
                  const q = CONDITION_QUESTIONS.find((c) => c.key === b.category);
                  const opt = q?.options.find((o) => o.value === b.answer);
                  return (
                    <div key={`${b.category}-${b.answer}`} data-testid={`lcost-breakdown-${b.category}-${b.answer}`} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-600">{isIssue ? "Problem" : q?.label}</span>
                      <span className="font-semibold text-slate-900 text-right">
                        {isIssue ? issueLabel(b.answer) : opt?.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <Button
              data-testid="lcost-book-pickup-btn"
              onClick={() => setStep(5)}
              className="mt-6 w-full h-12 btn-brand text-white font-semibold text-base rounded-xl"
            >
              Book free doorstep pickup →
            </Button>
            <button
              onClick={() => setStep(3)}
              className="mt-3 w-full text-sm text-slate-500 hover:text-slate-900"
            >
              Change my answers
            </button>
          </div>
        )}

        {/* STEP 5 - PICKUP */}
        {step === 5 && quote && (
          <div className="step-slide">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Book your free pickup
            </h1>
            <p className="mt-2 text-slate-600 text-sm">
              Our executive will visit and pay you {formatINR(quote.amount)} on the spot.
            </p>
            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-sm font-semibold">Full name</Label>
                <Input
                  data-testid="lcost-pickup-input-name"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  placeholder="e.g. Rohit Sharma"
                  className="mt-1.5 h-11 bg-white"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Phone number</Label>
                  <Input
                    data-testid="lcost-pickup-input-phone"
                    value={customer.phone}
                    onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                    placeholder="10-digit mobile"
                    className="mt-1.5 h-11 bg-white"
                    inputMode="numeric"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Email</Label>
                  <Input
                    data-testid="lcost-pickup-input-email"
                    type="email"
                    value={customer.email}
                    onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                    placeholder="For confirmation"
                    className="mt-1.5 h-11 bg-white"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Pickup address</Label>
                <Textarea
                  data-testid="lcost-pickup-textarea-address"
                  value={customer.address}
                  onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
                  placeholder="Flat, building, street, area, pincode"
                  className="mt-1.5 bg-white min-h-24"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">City</Label>
                  <Select
                    value={customer.city}
                    onValueChange={(v) => setCustomer({ ...customer, city: v })}
                  >
                    <SelectTrigger
                      data-testid="lcost-pickup-select-city"
                      className="mt-1.5 h-11 bg-white"
                    >
                      <SelectValue placeholder="Choose city" />
                    </SelectTrigger>
                    <SelectContent>
                      {meta.cities.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-semibold">Pickup date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="lcost-pickup-input-date"
                        className="mt-1.5 w-full h-11 bg-white justify-start font-normal"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2 text-slate-500" />
                        {pickupDate ? format(pickupDate, "PPP") : "Choose date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={pickupDate}
                        onSelect={setPickupDate}
                        disabled={(d) => d < new Date() || d > addDays(new Date(), 14)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Time slot</Label>
                <Select value={pickupSlot} onValueChange={setPickupSlot}>
                  <SelectTrigger
                    data-testid="lcost-pickup-select-slot"
                    className="mt-1.5 h-11 bg-white"
                  >
                    <SelectValue placeholder="Choose slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {meta.time_slots.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button
              data-testid="lcost-pickup-submit-btn"
              onClick={submitOrder}
              disabled={submitting}
              className="mt-7 w-full h-12 btn-brand text-white font-semibold text-base rounded-xl"
            >
              {submitting ? "Booking…" : `Confirm pickup · ${formatINR(quote.amount)}`}
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
